import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { setPageMeta } from "@/lib/seoMeta";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Search, Facebook, Twitter, Send, Youtube, Instagram } from "lucide-react";
import PlayPulse from "@/components/PlayPulse";
import SectionBar from "@/components/SectionBar";
import LiveSearch from "@/components/LiveSearch";
import BreakingTicker from "@/components/BreakingTicker";
import Logo from "@/components/Logo";
import { applySiteIdentity, DEFAULT_SITE_NAME } from "@/lib/siteIdentity";
import FollowUs from "@/components/FollowUs";
import ShareSiteButton from "@/components/ShareSiteButton";
import NotificationBell from "@/components/NotificationBell";
import BellOnboardingCard from "@/components/BellOnboardingCard"; // push opt-in card — keep with the bells below
import FollowBadge from "@/components/FollowBadge";
import NewsUpdatedBadge from "@/components/NewsUpdatedBadge";
import ThemeToggle from "@/components/ThemeToggle";
import LiveUpdatesBanner from "@/components/LiveUpdatesBanner";
import GazaDateTime from "@/components/GazaDateTime";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import GaTracker from "@/components/GaTracker";
import { TikTokIcon, WhatsAppIcon } from "@/components/icons/TikTokIcon";
import { normalizeWhatsAppUrl } from "@/lib/social";
import ContactButtons from "@/components/ContactButtons";
import { useLiveArticles, refreshLiveArticles } from "@/hooks/useLiveArticles";
import { PUBLIC_NAV_ITEMS } from "@/lib/sections";
import { useSavedList } from "@/lib/savedArticles";
import { LangContext, makeLangValue, setCurrentLang, sectionName, saveLang, PREFIX_RE } from "@/lib/i18n";
import LanguageSwitch from "@/components/LanguageSwitch";

export default function PublicLayout({ lang = "ar" }) {
    const [settings, setSettings] = useState({ site_name: DEFAULT_SITE_NAME.ar });
    applySiteIdentity(settings);
    const L = useMemo(() => makeLangValue(lang), [lang, settings.site_name, settings.site_name_en]);   // stable identity; re-made only when the language or the site name changes
    setCurrentLang(L.lang);
    const [sections, setSections] = useState([]);
    const savedItems = useSavedList();
    const navLabel = (item) => {
        if (item.kind === "saved") return <span className="inline-flex items-center gap-1.5">{L.t("saved")}{savedItems.length > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-red text-white text-[10px] font-black leading-none" data-testid="nav-saved-count">{savedItems.length}</span>}</span>;
        if (item.key === "reels") return <span className="inline-flex items-center gap-2"><PlayPulse />{L.t("reels")}</span>;
        if (item.kind !== "section") return L.lang !== "ar" ? L.t(item.key) : item.label;
        const s = sections.find((x) => x.slug === item.key) || { slug: item.key, name_ar: item.label };
        return sectionName(s, L.lang);
    };
    const withLang = (to) => (L.p ? (to === "/" ? `${L.p}/` : L.p + to) : to);
    const [tickerArticles, setTickerArticles] = useState([]);   // top-3 latest published articles
    const navigate = useNavigate();

    // Start the shared live poller so every surface can react in real time.
    useLiveArticles(15000);
    // Refresh on every SPA navigation (direct entry, internal nav, back/forward).
    const location = useLocation();
    // <html lang/dir> follow the route language; theme is untouched.
    useEffect(() => {   // article/home pages manage their own canonical; other pages get language-aware canonical + hreflang here
        const bare = location.pathname.replace(PREFIX_RE, "") || "/";
        if (bare !== "/" && !bare.startsWith("/article/")) setPageMeta(location.pathname, location.search);
    }, [location.pathname, location.search]);
    // Pre-render readiness: pages flip this to true once their data is in the DOM (fallback after 2.5s for static pages).
    useEffect(() => {
        window.prerenderReady = false;
        const h = setTimeout(() => { window.prerenderReady = true; }, 2500);
        return () => clearTimeout(h);
    }, [location.pathname]);

    useEffect(() => {
        document.documentElement.lang = L.lang;
        document.documentElement.dir = L.dir;
        return () => { document.documentElement.lang = "ar"; document.documentElement.dir = "rtl"; };
    }, [L.lang, L.dir]);
    // The URL decides the language: "/" is always Arabic and "/en/" always English — browser settings or a saved choice never
    // rewrite a page's language without navigating to its own URL (the language switch does that explicitly).
    useEffect(() => { if (L.lang !== "ar") saveLang(L.lang); }, [L.lang]);
    useEffect(() => { refreshLiveArticles("route:" + location.pathname); }, [location.pathname]);
    // Service-worker fallback: route the open tab to the clicked notification's article.
    useEffect(() => {
        const onMsg = (e) => { if (e.data?.type === "navigate" && typeof e.data.url === "string") navigate(e.data.url); };
        navigator.serviceWorker?.addEventListener("message", onMsg);
        return () => navigator.serviceWorker?.removeEventListener("message", onMsg);
    }, [navigate]);

    // Normalise Instagram URL to https + ensure external links use safe rel.
    const ensureHttps = (u) => u ? String(u).replace(/^http:\/\//i, "https://") : u;
    const igUrl = ensureHttps(settings.social_instagram);

    useEffect(() => {
        api.get("/public/sections").then((r) => setSections(r.data)).catch(() => {});
        api.get("/public/settings").then((r) => setSettings(r.data || {})).catch(() => {});

        // Top-3 latest published articles — the ticker mirrors this list.
        // Reuses the same endpoint the shared poller polls, so no extra
        // request cadence is introduced.
        const loadLatest = async () => {
            try {
                const { data } = await api.get("/public/latest?limit=3");
                if (Array.isArray(data)) setTickerArticles(data.slice(0, 3));
            } catch { /* keep previous ticker */ }
        };
        loadLatest();

        // Keep the ticker in sync with the shared poller's snapshot. `articles:list`
        // fires on every successful tick with the whole top-N list, so we can
        // simply take the first three every time — that automatically handles
        // new publishes, edits, deletions and re-orderings without a second
        // request. The swap is state-only and does NOT touch scroll position.
        const onList = (e) => {
            const items = Array.isArray(e?.detail) ? e.detail : null;
            if (!items) return;
            setTickerArticles(items.slice(0, 3));
        };
        const onApply = () => loadLatest();
        window.addEventListener("articles:list", onList);
        window.addEventListener("articles:apply", onApply);
        return () => {
            window.removeEventListener("articles:list", onList);
            window.removeEventListener("articles:apply", onApply);
        };
    }, []);

    const waUrl = normalizeWhatsAppUrl(settings.social_whatsapp);

    return (
        <LangContext.Provider value={L}>
        <div className={`min-h-screen flex flex-col bg-white public-scope lang-${L.lang}`} dir={L.dir}>
            <AnalyticsTracker />
            <GaTracker />
            {/* Top bar */}
            <div className="topbar text-brand-black text-xs border-b border-gray-200">
                {/* Mobile: full date on its own centered line, controls wrap below. Desktop: single row. */}
                <div className="mx-auto max-w-7xl px-3 sm:px-4 py-1 md:py-1.5 flex flex-col md:flex-row items-center md:justify-between gap-1 md:gap-3">
                    <div className="w-full md:w-auto md:shrink-0 flex justify-center md:justify-start">
                        <span className="topbar-datetime-group" data-testid="topbar-datetime-group"><GazaDateTime /><NewsUpdatedBadge /></span>
                    </div>
                    <div className="topbar-controls flex flex-wrap items-center justify-center md:justify-end gap-1.5 w-full md:w-auto md:flex-1 md:min-w-0" data-testid="topbar-controls">
                        {/* Social group: «تابعونا» label first (flow start = right in RTL, left in LTR), then the platform icons in their existing order */}
                        <span className="beam-ring beam-ring--slow rounded-[7px] social-ring" data-testid="social-ring">
                        <span className="social-group social-frame inline-flex flex-wrap items-center justify-center" data-testid="social-group" role="group" aria-label={L.t("followBadge")}>
                        <FollowBadge />
                        {settings.social_facebook && <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" className="social-btn social-btn--facebook glow-target" aria-label={L.t("facebook")} title={L.t("facebook")} data-testid="social-facebook"><Facebook /></a>}
                        {settings.social_telegram && <a href={settings.social_telegram} target="_blank" rel="noopener noreferrer" className="social-btn social-btn--telegram glow-target" aria-label={L.t("telegram")} title={L.t("telegram")} data-testid="social-telegram"><Send /></a>}
                        {waUrl && <a href={waUrl} target="_blank" rel="noopener noreferrer" className="social-btn social-btn--whatsapp glow-target" aria-label={L.t("whatsapp")} title={L.t("whatsapp")} data-testid="social-whatsapp"><WhatsAppIcon /></a>}
                        {settings.social_instagram && <a href={igUrl} target="_blank" rel="noopener noreferrer" className="social-btn social-btn--instagram glow-target" aria-label={L.t("instagram")} title={L.t("instagram")} data-testid="social-instagram"><Instagram /></a>}
                        {settings.social_tiktok && <a href={settings.social_tiktok} target="_blank" rel="noopener noreferrer" className="social-btn social-btn--tiktok glow-target" aria-label={L.t("tiktok")} title={L.t("tiktok")} data-testid="social-tiktok"><TikTokIcon /></a>}
                        {settings.social_twitter && <a href={settings.social_twitter} target="_blank" rel="noopener noreferrer" className="social-btn social-btn--twitter glow-target" aria-label={L.t("twitter")} title={L.t("twitter")} data-testid="social-twitter"><Twitter /></a>}
                        {settings.social_youtube && <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" className="social-btn social-btn--youtube glow-target" aria-label={L.t("youtube")} title={L.t("youtube")} data-testid="social-youtube"><Youtube /></a>}
                        </span>
                        </span>
                        {/* fixed visual order in both languages: sun/moon on the left, languages on the right */}
                        <span dir="ltr" className="inline-flex items-center gap-1.5 topbar-lang-theme"><ThemeToggle /><LanguageSwitch /></span>
                        <span className="hidden lg:inline-flex">
                            <BellOnboardingCard cardClass="hidden lg:block" idSuffix="-desktop">
                                <NotificationBell className="text-brand-black hover:text-brand-red" />
                            </BellOnboardingCard>
                        </span>
                    </div>
                </div>
            </div>

            {/* Main header */}
            <header className="site-header sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-200">
                <div className="mx-auto max-w-7xl px-4 py-2 md:py-3 flex items-center justify-between gap-3 md:gap-4">
                    <Link to={withLang("/")} className="flex items-center gap-2.5 md:gap-3 min-w-0 flex-shrink" data-testid="site-logo">
                        <Logo logoUrl={settings.logo_url} size="h-10 w-10 md:h-11 md:w-11" textSize="text-base md:text-lg" />
                        {(() => {
                            const raw = L.t("siteName").trim();
                            const parts = raw.split(/\s*[–—-]\s*/);
                            const main = parts[0] || raw;
                            const sub = parts.length > 1 ? parts.slice(1).join(" ").trim() : "غزة";
                            return (
                                <div className="min-w-0 leading-tight">
                                    <div className="font-display font-black text-brand-black whitespace-nowrap text-[13px] xs:text-[15px] sm:text-xl md:text-2xl" data-testid="site-name">
                                        {main} <span className="mx-0.5">-</span> <span className="text-brand-red" data-testid="site-name-sub">{sub}</span>
                                    </div>
                                </div>
                            );
                        })()}
                    </Link>

                    <div className="hidden lg:block flex-1 max-w-md"><LiveSearch compact testId="header-search" /></div>

                    <div className="lg:hidden flex items-center gap-0.5">
                        <Link to={withLang("/search")} className="glow-target rounded-md p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-brand-black" aria-label={L.t("search")} data-testid="mobile-search-link"><Search className="w-5 h-5" /></Link>
                        <BellOnboardingCard cardClass="lg:hidden" idSuffix="-mobile">
                            <NotificationBell className="text-brand-black hover:text-brand-red" />
                        </BellOnboardingCard>
                    </div>
                </div>

                {/* Nav */}
                <SectionBar withLang={withLang} navLabel={navLabel} />
                <nav className="hidden lg:block border-t border-gray-100 bg-white">
                    <div className="mx-auto max-w-7xl px-4">
                        {/* Desktop top nav — EXACTLY 3 items per product spec:
                            الرئيسية | غزة وفلسطين | العالم. About / Contact
                            are surfaced in the mobile sheet + footer only.
                            `justify-start` in a `dir=rtl` document places the
                            first item on the right edge (Arabic reading order)
                            while keeping equal `gap-6` spacing between items. */}
                        <ul className="hidden lg:flex items-center justify-start gap-4 lg:gap-6 py-2 whitespace-nowrap" data-testid="primary-nav">
                            {PUBLIC_NAV_ITEMS.map((item) => (
                                <li key={item.key}>
                                    <NavLink to={withLang(item.to)} end={item.end} className={({ isActive }) => `nav-frame px-3 py-2 text-sm font-bold ${isActive ? "nav-frame--active" : ""}`} data-testid={`nav-${item.key}`}>{navLabel(item)}</NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                </nav>

                {/* Breaking ticker — plain red bar, no light line/frame around it (product decision 2026-09-26) */}
                {tickerArticles.length > 0 && (
                    <BreakingTicker articles={tickerArticles} />
                )}
            </header>

            <main className="flex-1">
                <LiveUpdatesBanner />
                <Outlet />
            </main>

            {/* Footer */}
            <footer className="mt-16 text-gray-700 border-t border-gray-200">
                <div className="mx-auto max-w-7xl px-4 py-12 grid grid-cols-1 md:grid-cols-12 gap-y-10 gap-x-10 lg:gap-x-14">
                    <div className="md:col-span-5 min-w-0">
                        <div className="flex items-center gap-3 mb-4 min-w-0">
                            <Logo logoUrl={settings.logo_url} size="h-10 w-10" />
                            {(() => {
                                const raw = L.t("siteName").trim();
                                const parts = raw.split(/\s*[–—-]\s*/);
                                const main = parts[0] || raw;
                                const sub = parts.length > 1 ? parts.slice(1).join(" ").trim() : "غزة";
                                return (
                                    <div className="font-display font-black text-brand-black whitespace-nowrap text-base md:text-[15px] lg:text-lg" data-testid="footer-site-name">
                                        {main} <span className="mx-0.5">-</span> <span className="text-brand-red">{sub}</span>
                                    </div>
                                );
                            })()}
                        </div>
                        <p className="text-sm leading-relaxed">{L.lang !== "ar" ? L.t("footerAbout") : (settings.about_text || L.t("footerAbout"))}</p>
                    </div>
                    <div className="md:col-span-2">
                        <h4 className="text-brand-black font-bold mb-3 font-display">{L.t("sections")}</h4>
                        <ul className="space-y-2 text-sm" data-testid="footer-sections-list">
                            {PUBLIC_NAV_ITEMS.filter((i) => i.kind === "section").map((item) => (
                                <li key={item.key}><Link to={withLang(item.to)} className="hover:text-brand-red inline-block py-0.5" data-testid={`footer-nav-${item.key}`}>{navLabel(item)}</Link></li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-3">
                        <h4 className="text-brand-black font-bold mb-3 font-display">{L.t("links")}</h4>
                        <ul className="space-y-2 text-sm">
                            {PUBLIC_NAV_ITEMS.filter((i) => i.kind === "page").map((item) => (
                                <li key={item.key}><Link to={withLang(item.to)} className="hover:text-brand-red" data-testid={`footer-nav-${item.key}`}>{navLabel(item)}</Link></li>
                            ))}
                            <li><Link to={withLang("/privacy")} className="hover:text-brand-red" data-testid="footer-privacy">{L.t("privacyPolicy")}</Link></li>
                            <li><Link to={withLang("/corrections")} className="hover:text-brand-red" data-testid="footer-corrections">{L.t("correctionsPolicy")}</Link></li>
                            <li><Link to={withLang("/search")} className="hover:text-brand-red">{L.t("search")}</Link></li>
                        </ul>
                    </div>
                    <div className="md:col-span-2">
                        <h4 className="text-brand-black font-bold mb-3 font-display">{L.t("follow")}</h4>
                        <div className="flex flex-wrap gap-1" data-testid="footer-social-list">
                            {settings.social_facebook && <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" aria-label={L.t("facebook")} title={L.t("facebook")} className="social-btn social-btn--facebook glow-target" data-testid="footer-social-facebook"><Facebook /></a>}
                            {settings.social_telegram && <a href={settings.social_telegram} target="_blank" rel="noopener noreferrer" aria-label={L.t("telegram")} title={L.t("telegram")} className="social-btn social-btn--telegram glow-target" data-testid="footer-social-telegram"><Send /></a>}
                            {waUrl && <a href={waUrl} target="_blank" rel="noopener noreferrer" aria-label={L.t("whatsapp")} title={L.t("whatsapp")} className="social-btn social-btn--whatsapp glow-target" data-testid="footer-social-whatsapp"><WhatsAppIcon /></a>}
                            {settings.social_instagram && <a href={igUrl} target="_blank" rel="noopener noreferrer" aria-label={L.t("instagram")} title={L.t("instagram")} className="social-btn social-btn--instagram glow-target" data-testid="footer-social-instagram"><Instagram /></a>}
                            {settings.social_tiktok && <a href={settings.social_tiktok} target="_blank" rel="noopener noreferrer" aria-label={L.t("tiktok")} title={L.t("tiktok")} className="social-btn social-btn--tiktok glow-target" data-testid="footer-social-tiktok"><TikTokIcon /></a>}
                            {settings.social_twitter && <a href={settings.social_twitter} target="_blank" rel="noopener noreferrer" aria-label={L.t("twitter")} title={L.t("twitter")} className="social-btn social-btn--twitter glow-target" data-testid="footer-social-twitter"><Twitter /></a>}
                            {settings.social_youtube && <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" aria-label={L.t("youtube")} title={L.t("youtube")} className="social-btn social-btn--youtube glow-target" data-testid="footer-social-youtube"><Youtube /></a>}
                        </div>
                    </div>
                </div>
                <div className="mx-auto max-w-7xl px-4 pb-6">
                    <h4 className="text-brand-black font-bold mb-3 font-display">{L.t("directContact")}</h4>
                    <ContactButtons compact testPrefix="footer-contact" />
                </div>
                <div className="mx-auto max-w-7xl px-4 pb-6"><FollowUs /></div>
                {/* Mobile (<768 px): native share button in place of the QR. */}
                <div
                    className="flex md:hidden justify-center px-4"
                    style={{ marginTop: "20px", paddingBottom: "24px" }}
                    data-testid="footer-share-wrap"
                >
                    <ShareSiteButton />
                </div>
                <div className="border-t border-gray-200">
                    <div className="mx-auto max-w-7xl px-4 py-4 text-xs sm:text-sm text-gray-500 text-center" dir="rtl">
                        <span data-testid="copyright" dir={L.dir} className="whitespace-normal break-words text-center">{L.t("rights")} <span dir="ltr" className="inline-block mx-1">© 2026</span> {L.t("forAli")}</span>
                    </div>
                </div>
            </footer>
        </div>
        </LangContext.Provider>
    );
}
